import { Fragment, useEffect, useId, useRef } from 'react'

import { cn } from '@/ui/lib/utils'

/** Spec 040: one line in the details panel. A `null` label makes it a full-width note. */
export interface QuantFeedRow {
  key: string
  label: string | null
  value: string
  /** Secondary text shown beside the value (for example the provider state). */
  muted?: string
  /** Full text or the raw UTC instant, shown on hover. */
  title?: string
  tone?: 'alert'
  lines?: 1 | 2
}

export interface QuantFeedView {
  /** `not_configured`: the box stays reserved but nothing shows. */
  hidden: boolean
  /** False only before the first answer. */
  interactive: boolean
  stateKey: string
  word: string
  dotTone: 'accent' | 'alert' | 'ink-50' | 'ink-25'
  dotFilled: boolean
  symbolsText: string
  symbolsTitle: string
  symbolsMuted: boolean
  buttonTitle: string
  announce: string
  lastSeen: boolean
  rows: QuantFeedRow[]
}

export type QuantFeedCoverage =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; rows: QuantFeedRow[] }

export interface QuantFeedIndicatorProps {
  view: QuantFeedView
  open: boolean
  onToggle: () => void
  onClose: () => void
  coverage: QuantFeedCoverage
}

const FILLED = {
  accent: 'bg-accent-fill',
  alert: 'bg-alert',
  'ink-50': 'bg-ink-50',
  'ink-25': 'bg-ink-25',
} as const
const HOLLOW = {
  accent: 'border border-accent-fill',
  alert: 'border border-alert',
  'ink-50': 'border border-ink-50',
  'ink-25': 'border border-ink-25',
} as const

function Dot({ tone, filled }: { tone: QuantFeedView['dotTone']; filled: boolean }) {
  return (
    <span
      aria-hidden="true"
      data-feed-dot
      className={cn('h-2 w-2 shrink-0', filled ? FILLED[tone] : HOLLOW[tone])}
    />
  )
}

const BOX = 'relative h-full w-[264px] shrink-0 self-stretch max-[899px]:w-[150px]'
const CAPTION = 'px-3 pt-2 pb-1 font-mono text-[9px] tracking-[0.2em] text-ink-50 uppercase'

function Row({ row, attr }: { row: QuantFeedRow; attr: string }) {
  const tall = row.lines === 2 || row.label === null
  const dataAttr = { [attr]: row.key }
  return (
    <div
      {...dataAttr}
      title={row.title ?? row.value}
      className={cn('flex items-center gap-2 px-3', tall ? 'h-9' : 'h-[22px]', row.tone === 'alert' && 'text-alert')}
    >
      {row.label !== null && <span className="w-[120px] shrink-0 truncate text-ink-50">{row.label}</span>}
      <span className={cn('min-w-0 flex-1', tall ? 'line-clamp-2' : 'truncate')}>
        {row.value}
        {row.muted ? <span className="text-ink-50">{`  ${row.muted}`}</span> : null}
      </span>
    </div>
  )
}

/**
 * Spec 040: the market-feed indicator in the Quant header and its details
 * panel. Presentational: the caller supplies the view model, the open flag and
 * the coverage rows. A fixed-size box in every state. The panel is a plain
 * `div role="region"` (never portalled, so it inherits the Quant hue).
 */
export function QuantFeedIndicator({ view, open, onToggle, onClose, coverage }: QuantFeedIndicatorProps) {
  const panelId = useId()
  const boxRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      closeRef.current()
      buttonRef.current?.focus()
    }
    function onPointer(event: PointerEvent) {
      if (boxRef.current && event.target instanceof Node && !boxRef.current.contains(event.target)) closeRef.current()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  if (view.hidden) {
    return <div data-quant-feed data-feed-state={view.stateKey} aria-hidden="true" className={BOX} />
  }

  const wordClass =
    'shrink-0 font-mono text-[9.5px] tracking-[0.2em] whitespace-nowrap text-[var(--subj-tx,var(--ink))] uppercase'
  const announcer = (
    <span role="status" data-feed-announce className="sr-only">
      {view.announce}
    </span>
  )

  if (!view.interactive) {
    return (
      <div data-quant-feed data-feed-state={view.stateKey} className={BOX}>
        <div className="flex h-full items-center gap-2 px-2">
          <Dot tone={view.dotTone} filled={view.dotFilled} />
          <span className={wordClass}>{view.word}</span>
        </div>
        {announcer}
      </div>
    )
  }

  return (
    <div ref={boxRef} data-quant-feed data-feed-state={view.stateKey} className={BOX}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Market feed status"
        aria-expanded={open}
        aria-controls={panelId}
        title={view.buttonTitle}
        onClick={onToggle}
        className="flex h-full w-full items-center gap-2 px-2 text-left"
      >
        <Dot tone={view.dotTone} filled={view.dotFilled} />
        <span className={wordClass}>{view.word}</span>
        <span
          title={view.symbolsTitle}
          className={cn(
            'min-w-0 flex-1 truncate font-mono text-[9.5px] tracking-[0.1em] max-[899px]:hidden',
            view.symbolsMuted ? 'text-ink-50' : 'text-[var(--subj-tx,var(--ink))]',
          )}
        >
          {view.symbolsText}
        </span>
      </button>
      {announcer}
      {open && (
        <div
          role="region"
          id={panelId}
          aria-label="Market feed details"
          data-feed-panel
          className="absolute top-full left-0 z-40 w-[340px] border border-ink-20 bg-paper pb-1 font-mono text-[11px] text-ink"
        >
          {view.rows.map((row) => (
            <Fragment key={row.key}>
              {view.lastSeen && row.key === 'symbols' && <div className={CAPTION}>Last seen</div>}
              <Row row={row} attr="data-feed-row" />
            </Fragment>
          ))}
          <div className={CAPTION}>Stored data</div>
          {coverage.status === 'loading' && (
            <div className="flex h-[22px] items-center px-3 text-ink-50">Reading stored data…</div>
          )}
          {coverage.status === 'error' && (
            <div className="flex h-[22px] items-center px-3 text-ink-50">Stored data unavailable.</div>
          )}
          {coverage.status === 'ready' &&
            coverage.rows.map((row) => <Row key={row.key} row={row} attr="data-feed-coverage-row" />)}
        </div>
      )}
    </div>
  )
}
